import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TableColumnSettings,
  TableSettingsKey,
  UserTableSettings,
} from 'app/types';
import { axiosInstance } from 'setup/axiosInstance';
import {
  mergeTableSettings,
  ResolvedTableSettings,
} from 'components/tableSettings';

export const useTableSettings = (
  tableKey: TableSettingsKey,
  defaultColumns: TableColumnSettings[],
) => {
  const [loading, setLoading] = useState(false);
  const [savedSettings, setSavedSettings] = useState<UserTableSettings>();
  const [error, setError] = useState('');

  const settings = useMemo(
    () =>
      mergeTableSettings(
        tableKey,
        defaultColumns,
        savedSettings?.tableKey === tableKey ? savedSettings : undefined,
      ),
    [defaultColumns, savedSettings, tableKey],
  );

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setSavedSettings(undefined);
      const { data } = await axiosInstance.get<UserTableSettings>(
        `/profile/table-settings/${tableKey}`,
      );
      setSavedSettings(data);
    } catch (e) {
      setError('Не вдалося завантажити налаштування таблиці.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tableKey]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = useCallback(
    async (nextSettings: ResolvedTableSettings) => {
      const { data } = await axiosInstance.put<UserTableSettings>(
        `/profile/table-settings/${tableKey}`,
        nextSettings,
      );
      setSavedSettings(data);
    },
    [tableKey],
  );

  const resetSettings = useCallback(async () => {
    await axiosInstance.delete(`/profile/table-settings/${tableKey}/reset`);
    setSavedSettings(undefined);
  }, [tableKey]);

  return {
    error,
    loading,
    reloadSettings: loadSettings,
    resetSettings,
    saveSettings,
    settings,
  };
};
